'use client';
import {
  privateQueryContext,
  privateQueryOptions,
} from '@eliadmin/core/src/utils/private-query-context';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchOrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

/** The pause after typing before the order is asked – as in the core claim and order status forms. */
const LOOKUP_DELAY_MS = 500;

const PLAUSIBLE_EMAIL = /^\S+@\S+\.\S+$/;

/** `idle` nothing to look up yet · `pending` typing or asking · `found` · `missing` the API knows no such order. */
export type LookupStatus = 'idle' | 'pending' | 'found' | 'missing';

/**
 * The guest order lookup of the claim and the order status form: `POST /store/orders/info` with the
 * order number and the e-mail (core `fetchOrderInfo`, same query key, a failed request counts as „no
 * such order“), asked 500 ms after the last keystroke once both are filled in. The e-mail has to look
 * like one (core: any two characters) – half a typed address is no reason to say „order does not
 * exist“. The result only counts for the values that are on the screen: while the customer edits
 * them the status is `pending` and no order is returned, so the form below never works with the
 * previous order.
 */
export function useOrderLookup(identifier: string, email: string) {
  const locale = useLocaleStore(state => state.currentLocale);
  const typedIdentifier = identifier.trim();
  const typedEmail = email.trim();
  const typed = useMemo(
    () => ({ identifier: typedIdentifier, email: typedEmail }),
    [typedIdentifier, typedEmail],
  );
  const debounced = useDebounce(typed, LOOKUP_DELAY_MS);

  const complete = typed.identifier.length > 1 && PLAUSIBLE_EMAIL.test(typed.email);
  const settled = debounced.identifier === typed.identifier && debounced.email === typed.email;
  const lookup = useQuery({
    queryKey: [CacheKey.ORDER_INFO, ...privateQueryContext(locale, locale, debounced)],
    ...privateQueryOptions,
    queryFn: () =>
      fetchOrderInfo({
        locale,
        identifier: debounced.identifier,
        email: debounced.email,
      }).catch(() => null),
    enabled: debounced.identifier.length > 1 && PLAUSIBLE_EMAIL.test(debounced.email),
  });

  if (!complete) return { status: 'idle' as const, order: null };
  if (!settled || lookup.isLoading) return { status: 'pending' as const, order: null };
  const order = lookup.data?.data ?? null;
  return order ? { status: 'found' as const, order } : { status: 'missing' as const, order: null };
}
